/**
 * SolarConfiguration - PV system configuration component
 *
 * Handles solar sizing, costs, export mode, and optimization settings.
 *
 * Supports two modes:
 * 1. Props mode (legacy): Pass individual values and setters as props
 * 2. Context mode (new): Uses React Hook Form context when available
 *
 * The component automatically detects which mode to use based on whether
 * it's wrapped in a FormProvider and whether props are provided.
 */

import React from 'react';
import {
  Box,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormControl,
  FormLabel,
  RadioGroup,
  Radio,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { vortxColors } from '@vortx/shared-ui';
import { useFormField } from './useFormField';

const SIZING_MODES = [
  { value: 'prescribed', label: 'Prescribed Size', description: 'Specify exact PV size' },
  { value: 'optimal', label: 'Optimize Size', description: 'Let REopt find optimal PV size' },
];

const EXPORT_MODES = [
  { value: 'none', label: 'No Export', description: 'No power exported to grid' },
  { value: 'net_meter', label: 'Net Metering', description: 'Full retail credit for exports' },
  { value: 'wholesale', label: 'Wholesale', description: 'Wholesale rate for exports' },
];

/**
 * SolarConfiguration Component
 *
 * @param {Object} props
 * @param {boolean} [props.usePv] - Whether PV is enabled (props mode)
 * @param {Function} [props.setUsePv] - PV enabled setter (props mode)
 * @param {string} [props.sizingMode] - 'optimal' or 'prescribed' (props mode)
 * @param {Function} [props.setSizingMode] - Sizing mode setter (props mode)
 * @param {string} [props.pvMaxKw] - Max PV size in kW (props mode)
 * @param {Function} [props.setPvMaxKw] - PV size setter (props mode)
 * @param {string} [props.pvCost] - PV installed cost per kW (props mode)
 * @param {Function} [props.setPvCost] - PV cost setter (props mode)
 * @param {string} [props.pvExportMode] - Export mode (props mode)
 * @param {Function} [props.setPvExportMode] - Export mode setter (props mode)
 * @param {Object} [props.vortxStyles] - Styling object
 */
function SolarConfiguration({
  // Props mode values (backward compatible)
  usePv: propUsePv,
  setUsePv,
  sizingMode: propSizingMode,
  setSizingMode,
  pvMaxKw: propPvMaxKw,
  setPvMaxKw,
  pvCost: propPvCost,
  setPvCost,
  pvExportMode: propPvExportMode,
  setPvExportMode,
  // Common props
  vortxStyles = {},
}) {
  // Get form fields (automatically uses context or props)
  const enabled = useFormField('solar.enabled', propUsePv, setUsePv);
  const sizingMode = useFormField('solar.sizingMode', propSizingMode, setSizingMode);
  const maxKw = useFormField('solar.maxKw', propPvMaxKw, setPvMaxKw);
  const costPerKw = useFormField('solar.costPerKw', propPvCost, setPvCost);
  const exportMode = useFormField('solar.exportMode', propPvExportMode, setPvExportMode);

  return (
    <Box sx={{
      border: `1px solid rgba(178, 140, 80, 0.2)`,
      borderRadius: 2,
      overflow: 'hidden',
      mb: 2
    }}>
      {/* Header with toggle */}
      <Box sx={{
        p: 2,
        bgcolor: enabled.value ? alpha(vortxColors.gold, 0.1) : 'background.paper',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={Boolean(enabled.value)}
              onChange={(e) => enabled.setValue(e.target.checked)}
              sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }}
            />
          }
          label={
            <Typography sx={{ fontWeight: 500, color: 'text.primary' }}>
              Solar PV System
            </Typography>
          }
        />
      </Box>

      {/* Configuration section (only shown when enabled) */}
      {enabled.value && (
        <Box sx={{ p: 2, borderTop: `1px solid rgba(178, 140, 80, 0.2)` }}>
          {/* Sizing Mode */}
          <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
            <FormLabel component="legend" sx={{ color: 'text.primary', fontWeight: 500, mb: 1 }}>
              Sizing Mode
            </FormLabel>
            <RadioGroup
              row
              value={sizingMode.value || 'prescribed'}
              onChange={(e) => sizingMode.setValue(e.target.value)}
            >
              {SIZING_MODES.map((mode) => (
                <FormControlLabel
                  key={mode.value}
                  value={mode.value}
                  control={<Radio sx={{ color: vortxColors.gold, '&.Mui-checked': { color: vortxColors.gold } }} />}
                  label={
                    <Box>
                      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>{mode.label}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{mode.description}</Typography>
                    </Box>
                  }
                />
              ))}
            </RadioGroup>
          </FormControl>

          {/* Size and Cost Inputs */}
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              label={sizingMode.value === 'prescribed' ? 'PV Size (kW)' : 'Max PV Size (kW)'}
              type="number"
              value={maxKw.value || ''}
              onChange={(e) => maxKw.setValue(e.target.value)}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...vortxStyles.textField, flex: 1 }}
              helperText={sizingMode.value === 'optimal' ? 'Maximum allowed size for optimization' : 'Exact size to install'}
              error={!!maxKw.error}
            />
            <TextField
              label="Installed Cost ($/kW)"
              type="number"
              value={costPerKw.value || ''}
              onChange={(e) => costPerKw.setValue(e.target.value)}
              inputProps={{ min: 0 }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...vortxStyles.textField, flex: 1 }}
              helperText="Total turnkey cost including installation"
              error={!!costPerKw.error}
            />
          </Box>

          {/* Export Mode */}
          <TextField
            select
            label="Export Mode"
            value={exportMode.value || 'none'}
            onChange={(e) => exportMode.setValue(e.target.value)}
            fullWidth
            sx={vortxStyles.textField}
            helperText="How excess solar production is valued"
            error={!!exportMode.error}
          >
            {EXPORT_MODES.map((mode) => (
              <MenuItem key={mode.value} value={mode.value}>
                <Box>
                  <Typography variant="body2" sx={{ color: 'text.primary' }}>{mode.label}</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>{mode.description}</Typography>
                </Box>
              </MenuItem>
            ))}
          </TextField>
        </Box>
      )}
    </Box>
  );
}

// Export constants for use elsewhere
export { SIZING_MODES, EXPORT_MODES };

export default SolarConfiguration;
